import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiDownload,
  FiExternalLink,
  FiLayers,
  FiMonitor,
  FiRefreshCw,
  FiSearch,
  FiSliders,
} from 'react-icons/fi'
import { useAuthContext } from '../contexts/AuthContext'
import GenerationLoader from '../components/GenerationLoader'
import * as presentationService from '../services/presentationService'
import { extractErrorMessage } from '../utils/errorHandler'
import './GeneratorPage.css'
import './PresentationGeneratorPage.css'

const MIN_PROMPT_LENGTH = 10
const MAX_PROMPT_LENGTH = 4000
const BROWSER_PAGE_SIZE = 10

const toArray = value => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }
  return [value].filter(Boolean)
}

const extractListItems = payload => {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.items)) return payload.items
  if (Array.isArray(payload.themes)) return payload.themes
  if (Array.isArray(payload.folders)) return payload.folders
  if (Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload.results)) return payload.results
  return []
}

const extractNextCursor = payload =>
  payload?.next ||
  payload?.after ||
  payload?.nextCursor ||
  payload?.cursor ||
  payload?.pageInfo?.nextCursor ||
  payload?.page?.after ||
  payload?.page?.next_cursor ||
  payload?.meta?.next ||
  null

const extractHasMore = payload => {
  if (typeof payload?.has_more === 'boolean') return payload.has_more
  if (typeof payload?.hasMore === 'boolean') return payload.hasMore
  if (typeof payload?.page?.has_more === 'boolean') return payload.page.has_more
  if (typeof payload?.pageInfo?.hasNextPage === 'boolean')
    return payload.pageInfo.hasNextPage
  return Boolean(extractNextCursor(payload))
}

const toStatusClass = status =>
  `status-${(status || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

const PresentationGeneratorPage = () => {
  const navigate = useNavigate()
  const { token } = useAuthContext()

  const [config, setConfig] = useState(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)

  const [prompt, setPrompt] = useState('')
  const [title, setTitle] = useState('')
  const [exportFormat, setExportFormat] = useState('pptx')
  const [numCards, setNumCards] = useState(10)
  const [cardSplit, setCardSplit] = useState('auto')
  const [themeId, setThemeId] = useState('')
  const [templateId, setTemplateId] = useState('')

  const [isGenerating, setIsGenerating] = useState(false)
  const [globalError, setGlobalError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)

  const [presentations, setPresentations] = useState([])
  const [statusLoadingId, setStatusLoadingId] = useState(null)
  const [copiedValue, setCopiedValue] = useState(null)

  const [themes, setThemes] = useState([])
  const [themeQuery, setThemeQuery] = useState('')
  const [isLoadingThemes, setIsLoadingThemes] = useState(false)
  const [themeError, setThemeError] = useState('')
  const [themeRawResponse, setThemeRawResponse] = useState(null)
  const [themePagination, setThemePagination] = useState({
    hasMore: false,
    nextCursor: null,
  })

  const [folders, setFolders] = useState([])
  const [folderQuery, setFolderQuery] = useState('')
  const [isLoadingFolders, setIsLoadingFolders] = useState(false)
  const [folderError, setFolderError] = useState('')
  const [folderRawResponse, setFolderRawResponse] = useState(null)
  const [folderPagination, setFolderPagination] = useState({
    hasMore: false,
    nextCursor: null,
  })

  const timerRef = useRef(null)
  const startTimeRef = useRef(null)

  const cardsRange = useMemo(() => {
    if (!config) {
      return { min: 1, max: 60 }
    }
    return { min: 1, max: config.max_num_cards || 60 }
  }, [config])

  const cardSplitOptions = useMemo(() => {
    if (!config?.card_split_modes?.length) {
      return ['auto', 'inputTextBreaks']
    }
    return config.card_split_modes
  }, [config])

  const isPromptTooShort = useMemo(
    () => prompt.trim().length > 0 && prompt.trim().length < MIN_PROMPT_LENGTH,
    [prompt]
  )

  const isPromptTooLong = useMemo(() => prompt.length > MAX_PROMPT_LENGTH, [prompt])

  const clearTimers = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    startTimeRef.current = null
  }

  useEffect(() => {
    if (!token) return
    const loadConfig = async () => {
      setIsLoadingConfig(true)
      try {
        const cfg = await presentationService.getPresentationConfig(token)
        setConfig(cfg)
        setExportFormat(cfg.default_export_format || 'pptx')
        setNumCards(cfg.default_num_cards || 10)
        setCardSplit(cfg.card_split_modes?.[0] || 'auto')
      } catch (err) {
        setGlobalError(
          extractErrorMessage(
            err,
            "Impossibile caricare la configurazione di Gamma. Verifica l'API key."
          )
        )
      } finally {
        setIsLoadingConfig(false)
      }
    }
    loadConfig()
  }, [token])

  useEffect(() => () => clearTimers(), [])

  const handleBack = () => {
    navigate(-1)
  }

  const normalizedThemeLabel = themeId ? themeId.trim() : ''

  const addPresentationEntry = useCallback((response, requestPayload) => {
    const warnings =
      toArray(response.metadata?.warnings) ||
      toArray(response.metadata?.gamma_raw_response?.warnings)

    setPresentations(prev => [
      {
        generationId: response.generation_id,
        status: response.status,
        exportFormat: response.export_format,
        exportUrl: response.export_url,
        gammaUrl: response.gamma_url,
        expiresAt: response.expires_at,
        prompt: requestPayload.prompt,
        title: requestPayload.title || '',
        requestedParameters: response.metadata?.requested_parameters || requestPayload,
        metadata: response.metadata || {},
        warnings,
        rawResponse: response.metadata?.gamma_raw_response || null,
        rawStatus: null,
        createdAt: response.metadata?.request_timestamp || new Date().toISOString(),
        lastChecked: new Date().toISOString(),
      },
      ...prev,
    ])
  }, [])

  const handleGenerate = async event => {
    event.preventDefault()
    if (!token || !config) return

    const trimmedPrompt = prompt.trim()
    if (!trimmedPrompt) {
      setGlobalError('Inserisci un prompt per creare la presentazione.')
      return
    }
    if (trimmedPrompt.length < MIN_PROMPT_LENGTH) {
      setGlobalError(`Il prompt deve contenere almeno ${MIN_PROMPT_LENGTH} caratteri.`)
      return
    }
    if (trimmedPrompt.length > MAX_PROMPT_LENGTH) {
      setGlobalError(`Il prompt supera il limite di ${MAX_PROMPT_LENGTH} caratteri.`)
      return
    }
    if (!config.is_configured) {
      setGlobalError('Il servizio Gamma non è configurato. Contatta un amministratore.')
      return
    }

    const payload = {
      prompt: trimmedPrompt,
      export_format: exportFormat,
      title: title.trim() || undefined,
      theme_id: themeId.trim() || undefined,
      template_id: templateId.trim() || undefined,
      num_cards: Number(numCards) || undefined,
      card_split: cardSplit || undefined,
    }

    setGlobalError('')
    setSuccessMessage('')
    setIsGenerating(true)
    setElapsedSeconds(0)

    startTimeRef.current = Date.now()
    timerRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        setElapsedSeconds(elapsed)
      }
    }, 1000)

    try {
      const response = await presentationService.generatePresentation(token, payload)
      addPresentationEntry(response, payload)
      setSuccessMessage(
        'Presentazione generata! Controlla qui sotto status, warning e link di download.'
      )
    } catch (err) {
      setGlobalError(
        extractErrorMessage(err, 'Errore durante la generazione della presentazione.')
      )
    } finally {
      setIsGenerating(false)
      clearTimers()
    }
  }

  const handleRefreshStatus = async generationId => {
    if (!token || !generationId) return
    setStatusLoadingId(generationId)
    try {
      const statusResponse = await presentationService.getPresentationStatus(
        token,
        generationId
      )
      setPresentations(prev =>
        prev.map(item =>
          item.generationId === generationId
            ? {
                ...item,
                status: statusResponse.status,
                exportUrl: statusResponse.export_url || item.exportUrl,
                gammaUrl: statusResponse.gamma_url || item.gammaUrl,
                expiresAt: statusResponse.expires_at || item.expiresAt,
                warnings: toArray(statusResponse.warnings) || item.warnings || [],
                rawStatus: statusResponse.raw_response || item.rawStatus,
                lastChecked: new Date().toISOString(),
              }
            : item
        )
      )
    } catch (err) {
      setGlobalError(
        extractErrorMessage(err, 'Impossibile aggiornare lo stato della generazione.')
      )
    } finally {
      setStatusLoadingId(null)
    }
  }

  const handleCopy = async value => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setCopiedValue(value)
      setTimeout(() => setCopiedValue(null), 2000)
    } catch (err) {
      console.warn('Clipboard copy failed', err)
    }
  }

  const fetchThemes = useCallback(
    async ({ query, after, append } = {}) => {
      if (!token) return
      setThemeError('')
      setIsLoadingThemes(true)
      try {
        const response = await presentationService.listPresentationThemes(token, {
          query,
          after,
          limit: BROWSER_PAGE_SIZE,
        })
        const items = extractListItems(response)
        setThemes(prev => (append ? [...prev, ...items] : items))
        setThemePagination({
          hasMore: extractHasMore(response),
          nextCursor: extractNextCursor(response),
        })
        setThemeRawResponse(response)
      } catch (err) {
        setThemeError(extractErrorMessage(err, 'Errore nel recupero dei temi Gamma.'))
      } finally {
        setIsLoadingThemes(false)
      }
    },
    [token]
  )

  const fetchFolders = useCallback(
    async ({ query, after, append } = {}) => {
      if (!token) return
      setFolderError('')
      setIsLoadingFolders(true)
      try {
        const response = await presentationService.listPresentationFolders(token, {
          query,
          after,
          limit: BROWSER_PAGE_SIZE,
        })
        const items = extractListItems(response)
        setFolders(prev => (append ? [...prev, ...items] : items))
        setFolderPagination({
          hasMore: extractHasMore(response),
          nextCursor: extractNextCursor(response),
        })
        setFolderRawResponse(response)
      } catch (err) {
        setFolderError(
          extractErrorMessage(err, 'Errore nel recupero delle cartelle Gamma.')
        )
      } finally {
        setIsLoadingFolders(false)
      }
    },
    [token]
  )

  useEffect(() => {
    if (!token || !config) return
    fetchThemes({})
    fetchFolders({})
  }, [token, config, fetchThemes, fetchFolders])

  const renderWarnings = warnings => {
    if (!warnings || warnings.length === 0) return null
    return (
      <div className="status-warning-list">
        {warnings.map((warning, index) => (
          <span key={`${warning}-${index}`} className="warning-pill">
            <FiAlertTriangle /> {warning}
          </span>
        ))}
      </div>
    )
  }

  const renderMetadataBlock = (label, data) => {
    if (!data || (typeof data === 'object' && !Object.keys(data).length)) {
      return null
    }
    return (
      <details className="metadata-details">
        <summary>{label}</summary>
        <pre className="raw-payload">{JSON.stringify(data, null, 2)}</pre>
      </details>
    )
  }

  return (
    <div className="generator-page-area presentation-generator">
      <div className="generator-header">
        <div className="header-controls">
          <button
            onClick={handleBack}
            className="back-button"
            aria-label="Torna indietro"
          >
            <FiArrowLeft />
          </button>
          <h1>
            <FiMonitor /> Workspace Presentazioni Gamma
          </h1>
          <button
            onClick={() => fetchThemes({ query: themeQuery.trim() || undefined })}
            className="settings-button"
            aria-label="Aggiorna Temi"
            title="Ricarica le liste Gamma"
          >
            <FiRefreshCw />
          </button>
        </div>

        {config && (
          <div className="presentation-config-grid">
            <div className="config-chip">
              <span>Formati supportati</span>
              <strong>{config.supported_export_formats?.join(', ') || 'pptx'}</strong>
            </div>
            <div className="config-chip">
              <span>Default cards</span>
              <strong>{config.default_num_cards}</strong>
            </div>
            <div className="config-chip">
              <span>Max cards</span>
              <strong>{config.max_num_cards}</strong>
            </div>
            <div className="config-chip">
              <span>Polling</span>
              <strong>
                {config.poll_interval_seconds}s • timeout {config.poll_timeout_seconds}s
              </strong>
            </div>
          </div>
        )}

        {!config?.is_configured && !isLoadingConfig && (
          <div className="info-banner warning">
            Gamma non è configurato. Sarà necessario impostare un API key per usare
            questa funzione.
          </div>
        )}
      </div>

      <div className="generator-content presentation-layout">
        <div className="generator-form-container presentation-form-container">
          <form onSubmit={handleGenerate} className="generator-form">
            <div className="presentation-form-card">
              <label className="prompt-label">
                Prompt di generazione
                <small>
                  Min {MIN_PROMPT_LENGTH} - Max {MAX_PROMPT_LENGTH} caratteri
                </small>
              </label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={6}
                className={`generator-textarea ${
                  isPromptTooShort || isPromptTooLong ? 'error-border' : ''
                }`}
                placeholder="Descrivi in dettaglio la presentazione da creare..."
                disabled={isGenerating || isLoadingConfig}
              />
              <div className="prompt-controls-row">
                <span
                  className={`char-counter ${isPromptTooLong ? 'limit-reached' : ''}`}
                >
                  {prompt.length} / {MAX_PROMPT_LENGTH}
                </span>
                {isPromptTooShort && (
                  <span className="char-warning">
                    Aggiungi qualche dettaglio in più per raggiungere il minimo.
                  </span>
                )}
              </div>
            </div>

            <div className="presentation-form-card">
              <div className="presentation-controls-grid">
                <div className="setting-group">
                  <label>
                    Titolo opzionale
                    <FiSliders />
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Es. Piano Strategico Q3"
                    className="presentation-input"
                    disabled={isGenerating}
                  />
                </div>

                <div className="setting-group">
                  <label>Formato export</label>
                  <select
                    value={exportFormat}
                    onChange={e => setExportFormat(e.target.value)}
                    className="presentation-select"
                    disabled={isGenerating}
                  >
                    {(config?.supported_export_formats || ['pptx', 'pdf']).map(
                      format => (
                        <option key={format} value={format}>
                          {format.toUpperCase()}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              <div className="presentation-controls-grid">
                <div className="setting-group">
                  <label>
                    Numero di slide ({cardsRange.min}-{cardsRange.max})
                  </label>
                  <input
                    type="range"
                    min={cardsRange.min}
                    max={cardsRange.max}
                    value={numCards}
                    onChange={e => setNumCards(Number(e.target.value))}
                    disabled={isGenerating}
                  />
                  <div className="range-value">{numCards} carte</div>
                </div>

                <div className="setting-group">
                  <label>Suddivisione carte</label>
                  <select
                    value={cardSplit}
                    onChange={e => setCardSplit(e.target.value)}
                    className="presentation-select"
                    disabled={isGenerating}
                  >
                    {cardSplitOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="presentation-controls-grid theme-inputs">
                <div className="setting-group">
                  <label>
                    Theme ID Gamma
                    {normalizedThemeLabel && (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => setThemeId('')}
                        disabled={isGenerating}
                      >
                        Rimuovi
                      </button>
                    )}
                  </label>
                  <input
                    type="text"
                    value={themeId}
                    onChange={e => setThemeId(e.target.value)}
                    placeholder="gamma-theme-123"
                    className="presentation-input"
                    disabled={isGenerating}
                  />
                </div>

                <div className="setting-group">
                  <label>Template ID Gamma (facoltativo)</label>
                  <input
                    type="text"
                    value={templateId}
                    onChange={e => setTemplateId(e.target.value)}
                    placeholder="gamma-template-xyz"
                    className="presentation-input"
                    disabled={isGenerating}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="generate-button"
              disabled={
                isGenerating ||
                isLoadingConfig ||
                !prompt.trim() ||
                isPromptTooShort ||
                isPromptTooLong ||
                !config?.is_configured
              }
            >
              {isGenerating ? (
                <>
                  <FiRefreshCw className="spinner-icon" />
                  <span>Invio a Gamma...</span>
                </>
              ) : (
                <>
                  <FiMonitor />
                  <span>Genera Presentazione</span>
                </>
              )}
            </button>
          </form>

          {globalError && (
            <div className="error-placeholder">
              <FiAlertTriangle className="error-icon" />
              <div>
                <h3>Errore</h3>
                <p>{globalError}</p>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="success-banner">
              <FiCheckCircle /> {successMessage}
            </div>
          )}
        </div>

        <div className="generator-output-container presentation-output">
          {isGenerating && (
            <GenerationLoader
              type="presentation"
              elapsedSeconds={elapsedSeconds}
              message="Gamma sta preparando il deck e verificando le risorse richieste..."
            />
          )}

          {!isGenerating && presentations.length === 0 && (
            <div className="presentation-empty-state">
              <FiMonitor />
              <h3>Nessuna presentazione ancora</h3>
              <p>
                Dopo aver inviato una richiesta a Gamma, i risultati appariranno qui con
                link diretti e stato aggiornabile.
              </p>
            </div>
          )}

          {presentations.length > 0 && (
            <div className="presentation-status-list">
              {presentations.map(item => (
                <div key={item.generationId} className="presentation-status-card">
                  <div className="status-card-header">
                    <div>
                      <span className="status-label">Generation ID</span>
                      <div className="status-id-row">
                        <code>{item.generationId}</code>
                        <button
                          type="button"
                          className="icon-only-button"
                          onClick={() => handleCopy(item.generationId)}
                          title="Copia ID"
                        >
                          {copiedValue === item.generationId ? (
                            <FiCheckCircle />
                          ) : (
                            <FiClipboard />
                          )}
                        </button>
                      </div>
                      <div className="status-title">
                        {item.title || 'Titolo non impostato'}
                      </div>
                    </div>
                    <div className={`status-pill ${toStatusClass(item.status)}`}>
                      {item.status || 'Sconosciuto'}
                    </div>
                  </div>

                  <div className="status-meta-grid">
                    <div>
                      <span className="status-label">Prompt</span>
                      <p className="status-prompt">
                        {item.prompt?.length > 180
                          ? `${item.prompt.substring(0, 180)}...`
                          : item.prompt}
                      </p>
                    </div>
                    <div>
                      <span className="status-label">Formato</span>
                      <strong>{item.exportFormat?.toUpperCase()}</strong>
                    </div>
                    <div>
                      <span className="status-label">Richiesto il</span>
                      <div className="status-datetime">
                        <FiClock /> {new Date(item.createdAt).toLocaleString('it-IT')}
                      </div>
                    </div>
                    <div>
                      <span className="status-label">Ultimo controllo</span>
                      <div className="status-datetime">
                        <FiClock />{' '}
                        {item.lastChecked
                          ? new Date(item.lastChecked).toLocaleString('it-IT')
                          : 'Mai'}
                      </div>
                    </div>
                    {item.expiresAt && (
                      <div>
                        <span className="status-label">Scadenza link</span>
                        <div className="status-datetime">
                          <FiClock /> {new Date(item.expiresAt).toLocaleString('it-IT')}
                        </div>
                      </div>
                    )}
                  </div>

                  {renderWarnings(item.warnings)}

                  <div className="status-actions">
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => handleRefreshStatus(item.generationId)}
                      disabled={statusLoadingId === item.generationId}
                    >
                      {statusLoadingId === item.generationId ? (
                        <FiRefreshCw className="spinner-icon" />
                      ) : (
                        <FiRefreshCw />
                      )}
                      Aggiorna stato
                    </button>

                    {item.exportUrl && (
                      <a
                        href={item.exportUrl}
                        className="link-button"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FiDownload /> Scarica deck
                      </a>
                    )}

                    {item.gammaUrl && (
                      <a
                        href={item.gammaUrl}
                        className="link-button"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FiExternalLink /> Apri in Gamma
                      </a>
                    )}
                  </div>

                  {renderMetadataBlock('Parametri inviati', item.requestedParameters)}
                  {renderMetadataBlock('Risposta Gamma', item.rawResponse)}
                  {renderMetadataBlock('Ultimo stato Gamma', item.rawStatus)}
                </div>
              ))}
            </div>
          )}

          <div className="browser-card">
            <div className="browser-card-header">
              <h3>
                <FiLayers /> Temi Gamma
              </h3>
              <div className="browser-actions">
                <div className="search-input-wrapper">
                  <FiSearch />
                  <input
                    type="text"
                    value={themeQuery}
                    onChange={e => setThemeQuery(e.target.value)}
                    placeholder="Cerca per nome"
                  />
                </div>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => fetchThemes({ query: themeQuery.trim() || undefined })}
                  disabled={isLoadingThemes}
                >
                  Cerca
                </button>
              </div>
            </div>

            {themeError && <div className="info-banner warning">{themeError}</div>}

            {isLoadingThemes ? (
              <div className="browser-loading">Caricamento temi...</div>
            ) : themes.length === 0 ? (
              <div className="browser-empty">
                Nessun tema trovato per il filtro corrente.
              </div>
            ) : (
              <ul className="browser-list">
                {themes.map(theme => {
                  const id = theme.id || theme.themeId || theme.theme_id
                  const name = theme.name || theme.title || theme.displayName
                  return (
                    <li key={id || JSON.stringify(theme)} className="browser-item">
                      <div>
                        <strong>{name || 'Tema senza nome'}</strong>
                        <small>{id}</small>
                      </div>
                      <div className="browser-item-actions">
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => setThemeId(id || '')}
                        >
                          Usa tema
                        </button>
                        <button
                          type="button"
                          className="icon-only-button"
                          onClick={() => handleCopy(id)}
                          title="Copia ID tema"
                        >
                          {copiedValue === id ? <FiCheckCircle /> : <FiClipboard />}
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}

            {themePagination.hasMore && (
              <button
                type="button"
                className="link-button secondary"
                onClick={() =>
                  fetchThemes({
                    query: themeQuery.trim() || undefined,
                    after: themePagination.nextCursor,
                    append: true,
                  })
                }
                disabled={isLoadingThemes}
              >
                Carica altri
              </button>
            )}

            {renderMetadataBlock('Payload temi', themeRawResponse)}
          </div>

          <div className="browser-card">
            <div className="browser-card-header">
              <h3>
                <FiMonitor /> Cartelle Gamma
              </h3>
              <div className="browser-actions">
                <div className="search-input-wrapper">
                  <FiSearch />
                  <input
                    type="text"
                    value={folderQuery}
                    onChange={e => setFolderQuery(e.target.value)}
                    placeholder="Cerca per nome"
                  />
                </div>
                <button
                  type="button"
                  className="link-button"
                  onClick={() =>
                    fetchFolders({ query: folderQuery.trim() || undefined })
                  }
                  disabled={isLoadingFolders}
                >
                  Cerca
                </button>
              </div>
            </div>

            {folderError && <div className="info-banner warning">{folderError}</div>}

            {isLoadingFolders ? (
              <div className="browser-loading">Caricamento cartelle...</div>
            ) : folders.length === 0 ? (
              <div className="browser-empty">
                Nessuna cartella trovata per il filtro corrente.
              </div>
            ) : (
              <ul className="browser-list">
                {folders.map(folder => {
                  const id = folder.id || folder.folderId || folder.folder_id
                  const name = folder.name || folder.title || folder.displayName
                  return (
                    <li key={id || JSON.stringify(folder)} className="browser-item">
                      <div>
                        <strong>{name || 'Cartella senza nome'}</strong>
                        <small>{id}</small>
                      </div>
                      <div className="browser-item-actions">
                        <button
                          type="button"
                          className="icon-only-button"
                          onClick={() => handleCopy(id)}
                          title="Copia ID cartella"
                        >
                          {copiedValue === id ? <FiCheckCircle /> : <FiClipboard />}
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}

            {folderPagination.hasMore && (
              <button
                type="button"
                className="link-button secondary"
                onClick={() =>
                  fetchFolders({
                    query: folderQuery.trim() || undefined,
                    after: folderPagination.nextCursor,
                    append: true,
                  })
                }
                disabled={isLoadingFolders}
              >
                Carica altri
              </button>
            )}

            {renderMetadataBlock('Payload cartelle', folderRawResponse)}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PresentationGeneratorPage
